import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { updateToastInfo } from '../../../actions/settings';
import { updateSpinnerState } from '../../../actions/spinner';
import { apiCall } from '../../../middlewares/api';
import { apiCallConsts } from '../../../constants/apiCallConsts';
import Request from './components/Request';
import { useTranslation } from 'react-i18next';
import { StyledSearchBar } from '../../trips/styles';
import { Button } from '@themesberg/react-bootstrap';
import ResponsiveModal from './components/ResponsiveModal';
import {
  faCheckCircle,
  faXmarkCircle,
} from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const SMSRequest = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState(data);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [modalState, setModalState] = useState({ show: false });
  const [blankScreen, setBlankScreen] = useState({ show: false, type: null });
  const [showInvalidLink, setShowInvalidLink] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [spinner, setSpinner] = useState(true);
  const [toastInfo, setToastInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  // const userName = useSelector((state) => state?.user?.user?.username || '');
  const [userName, setUserName] = useState('');

  const fetchRequests = async () => {
    try {
      // dispatch(updateSpinnerState(true));
      setSpinner(true);
      setErrMsg('');
      const data = await apiCall(
        apiCallConsts.GET_METHOD,
        'internal/pricing/sms',
        { id }
      );
      // if (data?.message) {
      //   dispatch(
      //     updateToastInfo({
      //       show: true,
      //       type: 'success',
      //       message: data.message,
      //     })
      //   );
      //   return;
      // }
      setData(data);
      setFilteredData(data);
      setUserName(data[0]?.approver);
    } catch (error) {
      if (error?.message?.includes('Link')) {
        setShowInvalidLink(true);
        return;
      }
      // dispatch(
      //   updateToastInfo({ show: true, type: 'danger', message: error.message })
      // );
      setErrMsg(error.message);
      setToastInfo({
        show: true,
        type: 'danger',
        message: error.message,
      });
    } finally {
      // dispatch(updateSpinnerState(false));
      setSpinner(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [id]);

  const updateRequest = async (updateData) => {
    try {
      // dispatch(updateSpinnerState(true));
      setSpinner(true);
      setErrMsg('');
      const res = await apiCall(
        apiCallConsts.PUT_METHOD,
        'internal/pricing/update',
        updateData
      );
      const updatedStatus = updateData.status;

      setToastInfo({
        show: true,
        type: 'success',
        message: res,
      });

      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          message: 'Your request has been successfully updated!',
        })
      );

      setSelectedRequests([]);
      await fetchRequests(); // 🔁 Always re-fetch after update

      // Show blank screen if no data is left after update
      setBlankScreen((prev) => {
        const remaining = data.filter(
          (item) => !updateData?.id?.includes(item.objectId)
        );
        return remaining.length === 0
          ? { show: true, type: updatedStatus }
          : { show: false, type: null };
      });
    } catch (error) {
      setErrMsg(error?.message);
      dispatch(
        updateToastInfo({ show: true, type: 'danger', message: error.message })
      );
    } finally {
      // dispatch(updateSpinnerState(false));
      setSpinner(false);
    }
  };

  const handleCheckBoxClick = (e) => {
    const { id, checked } = e.target;
    const updatedData = data.map((item) =>
      item.objectId === id ? { ...item, isSelected: checked } : item
    );
    setData(updatedData);
    setSelectedRequests(updatedData.filter((item) => item.isSelected));
  };

  const handleActionClick = (type, isMulti, id) => {
    // Added id parameter
    const ids = isMulti ? selectedRequests.map((each) => each.objectId) : [id]; // Use id if not multi-select
    setBlankScreen((prev) => ({
      ...prev,
      type: type === 'approve' ? 'approved' : 'rejected',
    }));
    setModalState({
      show: true,
      icon: type === 'approve' ? faCheckCircle : faXmarkCircle,
      title: type === 'approve' ? 'Approve' : 'Reject',
      subtitle: isMulti
        ? `Are you sure you want to ${
            type === 'approve' ? 'approve' : 'reject'
          } all the selected requests?`
        : `Are you sure you want to ${
            type === 'approve' ? 'approve' : 'reject'
          } this request?`,
      onConfirm: () => {
        updateRequest({
          id: ids,
          status: type === 'approve' ? 'approved' : 'reject',
          userName,
        });
        setModalState({ show: false });
      },
      onCancel: () => setModalState({ show: false }),
    });
  };

  const handleSelectAll = () => {
    const allSelected = data.every((item) => item.isSelected);
    const updatedData = data.map((item) => ({
      ...item,
      isSelected: !allSelected,
    }));
    setData(updatedData);
    setSelectedRequests(updatedData.filter((item) => item.isSelected));
  };

  useEffect(() => {
    const filtered = data.filter((item) =>
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredData(filtered);
  }, [data, searchTerm]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <div className="m-3">
      <div className="fw-medium text-md-start text-center fs-5">
        {t('Price Change Requests')}
      </div>
      <div className="d-flex bg-white p-3 flex-column gap-3 flex-md-row justify-content-center align-items-center w-100">
        <StyledSearchBar
          width={'21rem'}
          placeholder={'Type to search'}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Button
          style={{
            backgroundColor:
              selectedRequests.length > 0 ? '#F3D6D7' : '#eaedf2',
            width: '21rem',
            color: 'black',
            border: 'none',
          }}
          className="rounded-0"
          onClick={handleSelectAll}
        >
          {t(
            data.length > 0
              ? `Select All (${data.length})`
              : `Selected (${selectedRequests.length}/${data.length})`
          )}
        </Button>
        {selectedRequests.length > 0 && (
          <div
            className="d-flex justify-content-between"
            style={{ width: '21rem' }}
          >
            <Button
              style={{
                flex: 1,
                marginRight: '0.5rem',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#F4F4F4',
                border: 'none',
                color: '#4A4A4A',
              }}
              onClick={() => handleActionClick('reject', true)}
            >
              {'Reject All'}
            </Button>
            <Button
              style={{
                flex: 1,
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#F4F4F4',
                border: 'none',
                color: '#4A4A4A',
              }}
              onClick={() => handleActionClick('approve', true)}
            >
              {'Approve All'}
            </Button>
          </div>
        )}
      </div>

      {spinner && <div className="p-4 fs-3 text-center">Loading...</div>}

      {toastInfo.show && (
        <div className={`toast toast-${toastInfo.type}`}>
          {toastInfo.message}
        </div>
      )}
      {errMsg && <h2 className="text-danger text-center p-4">{errMsg}</h2>}
      {filteredData?.length > 0 ? (
        filteredData.map((each) => (
          <div
            className="d-flex flex-column gap-3 flex-md-row flex-md-wrap justify-content-center align-items-center justify-content-md-start mt-3 w-100"
            key={each.objectId}
          >
            <Request
              key={each.objectId}
              requestData={each}
              handleApproveClick={(e) =>
                handleActionClick('approve', false, e.target.id)
              } // Pass id for single item action
              handleRejectClick={(e) =>
                handleActionClick('reject', false, e.target.id)
              } // Pass id for single item action
              handleCheckBoxClick={handleCheckBoxClick}
            />
          </div>
        ))
      ) : blankScreen.show ? (
        <div className="d-flex mt-3 p-5 gap-4 flex-column justify-content-center align-items-center">
          <FontAwesomeIcon
            size="5x"
            color="#E2212B"
            icon={
              blankScreen.type === 'approved' ? faCheckCircle : faXmarkCircle
            }
          />
          <div className="text-center text-wrap">
            {blankScreen.type === 'approved'
              ? 'All the selected requests have been successfully approved.'
              : 'All the selected requests have been successfully rejected.'}
          </div>
        </div>
      ) : showInvalidLink ? (
        <div className="p-4 fs-3 text-center text-danger">
          Link is no longer valid!
        </div>
      ) : (
        !spinner && <div className="p-4 fs-3 text-center">No Data Found</div>
      )}

      {modalState?.show && (
        <ResponsiveModal
          show={modalState.show}
          icon={modalState.icon}
          title={modalState.title}
          subtitle={modalState.subtitle}
          onConfirm={modalState.onConfirm}
          onCancel={modalState.onCancel}
        />
      )}
    </div>
  );
};

export default SMSRequest;
